import { Link, useLocation, useNavigate } from "react-router-dom";
import { getUser, logoutRemote, api } from "@/lib/api";
import { getHaatId, setHaatId } from "@/lib/haat";
import BrandLogo from "@/components/BrandLogo";
import SyncBanner from "@/components/SyncBanner";
import { useT } from "@/lib/i18n";
import LanguageSelect from "@/components/LanguageSelect";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TEST } from "@/constants/testIds";
import {
  LayoutDashboard, Users, Beef, FileText, Settings, LogOut,
  UserPlus, BadgeIndianRupee, QrCode, LifeBuoy, CreditCard,
  Wallet, Menu, X, Globe, PlayCircle, Store, BarChart3, ShieldCheck, Truck, Stethoscope, Wheat,
} from "lucide-react";
import { useState, useEffect } from "react";

const NAVS = {
  super_admin: [
    { key: "dashboard", to: "/admin", labelKey: "nav.overview", icon: LayoutDashboard },
    { key: "subscribers", to: "/admin/subscribers", labelKey: "nav.subscribers", icon: Users },
    { key: "plans", to: "/admin/plans", labelKey: "nav.plans", icon: CreditCard },
    { key: "master", to: "/admin/master", labelKey: "nav.masterData", icon: Beef },
    { key: "geo", to: "/admin/geo", labelKey: "nav.geoAccess", icon: Globe },
    { key: "security", to: "/admin/security", labelKey: "nav.security", icon: ShieldCheck },
    { key: "transport", to: "/admin/transport", labelKey: "nav.transport", icon: Truck },
    { key: "vets", to: "/admin/vets", labelKey: "nav.vets", icon: Stethoscope },
    { key: "feed", to: "/admin/feed", labelKey: "nav.feed", icon: Wheat },
    { key: "tickets", to: "/admin/tickets", labelKey: "nav.support", icon: LifeBuoy },
    { key: "help", to: "/admin/help", labelKey: "nav.help", icon: PlayCircle },
  ],
  subscriber: [
    { key: "dashboard", to: "/subscriber", labelKey: "nav.overview", icon: LayoutDashboard },
    { key: "helpers", to: "/subscriber/helpers", labelKey: "nav.helpers", icon: UserPlus },
    { key: "animals", to: "/subscriber/animals", labelKey: "nav.animals", icon: Beef },
    { key: "find-vet", to: "/subscriber/find-vet", labelKey: "nav.findVet", icon: Stethoscope },
    { key: "reports", to: "/subscriber/reports", labelKey: "nav.reports", icon: FileText },
    { key: "settings", to: "/subscriber/settings", labelKey: "nav.settings", icon: Settings },
    { key: "help", to: "/help", labelKey: "nav.help", icon: PlayCircle },
  ],
  helper: [
    { key: "dashboard", to: "/helper", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { key: "new-registration", to: "/helper/new-registration", labelKey: "nav.newRegistration", icon: UserPlus },
    { key: "gatepasses", to: "/helper/gatepasses", labelKey: "nav.gatepasses", icon: QrCode },
    { key: "reports", to: "/helper/reports", labelKey: "nav.reports", icon: FileText },
    { key: "transport", to: "/helper/transport", labelKey: "nav.transport", icon: Truck },
    { key: "feed", to: "/helper/feed", labelKey: "nav.feed", icon: Wheat },
    { key: "help", to: "/help", labelKey: "nav.help", icon: PlayCircle },
  ],
  driver: [
    { key: "dashboard", to: "/driver", labelKey: "nav.transport", icon: Truck },
  ],
  vet: [
    { key: "dashboard", to: "/vet", labelKey: "nav.vetDashboard", icon: Stethoscope },
  ],
};

// Multi-haat meta cached per session so the shell doesn't refetch on every page.
let _haatMeta = null;

export default function DashboardShell({ children, title, subtitle, action }) {
  const user = getUser();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [haatMeta, setHaatMeta] = useState(_haatMeta);
  const { t } = useT();

  useEffect(() => {
    if (user?.role !== "subscriber" || _haatMeta) return;
    api.get("/subscriber/haats/meta").then((r) => {
      _haatMeta = r.data;
      const ids = (r.data.haats || []).map((h) => h.haat_id);
      if (ids.length && !ids.includes(getHaatId())) setHaatId(ids[0]);
      setHaatMeta(r.data);
    }).catch(() => {});
  }, [user?.role]);

  let items = NAVS[user?.role] || [];
  if (user?.role === "subscriber" && haatMeta?.multi) {
    items = [
      { key: "myhaats", to: "/subscriber/haats", labelKey: "nav.myHaats", icon: Store },
      { key: "combined", to: "/subscriber/combined", labelKey: "nav.combined", icon: BarChart3 },
      ...items,
    ];
  }

  const switchHaat = (id) => {
    setHaatId(id);
    _haatMeta = null;
    window.location.reload(); // every module re-fetches with the new X-Haat-Id
  };

  const logout = async () => {
    await logoutRemote();
    nav("/login");
  };

  const roleLabel = user?.role ? t(`role.${user.role}`) : "";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 inset-y-0 z-40 w-72 bg-card border-r flex flex-col transition-transform`}>
        <div className="p-6 border-b">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo size="sidebarExpanded" alt="AnimalMela" />
            <div>
              <div className="font-display font-extrabold text-lg">AnimalMela</div>
              <div className="text-xs text-muted-foreground">{roleLabel}</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {items.map((it) => {
            const Icon = it.icon;
            const active = loc.pathname === it.to;
            return (
              <Link
                key={it.key}
                to={it.to}
                data-testid={TEST.shellNav(it.key)}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-full text-sm font-semibold transition-all ${
                  active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t(it.labelKey)}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t">
          <div className="text-sm font-semibold">{user?.name}</div>
          <div className="text-xs text-muted-foreground mb-3 font-mono">{user?.mobile}</div>
          <Button
            data-testid={TEST.shellLogout}
            onClick={logout}
            variant="outline"
            className="w-full rounded-full"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("common.signOut")}
          </Button>
        </div>
      </aside>

      {/* Backdrop */}
      {open && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="am-dash-mobile-header sticky top-0 z-20 bg-background/95 backdrop-blur border-b">
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2 px-4 sm:px-6 py-3 sm:py-4">
            <button className="md:hidden p-2 rounded-xl hover:bg-muted" onClick={() => setOpen(!open)}>
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-xl sm:text-2xl font-bold truncate">{title}</h1>
              {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
            </div>
            {user?.role === "subscriber" && (haatMeta?.haats?.length || 0) > 1 && (
              <Select value={getHaatId()} onValueChange={switchHaat}>
                <SelectTrigger className="w-40 sm:w-48 rounded-full h-9 text-sm font-semibold" data-testid="haat-switcher">
                  <Store className="w-4 h-4 mr-1 shrink-0 text-primary" />
                  <SelectValue placeholder={t("haat.current")} />
                </SelectTrigger>
                <SelectContent>
                  {haatMeta.haats.map((h) => (
                    <SelectItem key={h.haat_id} value={h.haat_id} data-testid={`haat-option-${h.haat_id}`}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="hidden sm:block">
              <LanguageSelect compact />
            </div>
            {/* Mobile: actions drop to their own full-width, horizontally scrollable row */}
            {action && (
              <div className="w-full sm:w-auto order-last sm:order-none flex items-center gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:none]">
                {action}
              </div>
            )}
          </div>
        </header>
        <div className="p-6">
          <SyncBanner visible />
          {children}
        </div>
      </main>
    </div>
  );
}
